// ControlValveConfigModal.jsx — PID/Control valve configuration (unified premium layout)
import React, { useState } from 'react';
import { X, Activity, Settings, Monitor } from 'lucide-react';
import SearchableTagSelect from './SearchableTagSelect';

const DEFAULT = {
  pv:   { tagName: '', min: 0, max: 100 },
  sp:   { tagName: '', min: 0, max: 100 },
  op:   { tagName: '', min: 0, max: 100 },
  openColor:  '#00ff00',
  closeColor: '#ff0000',
  mode: { tagName: '' },
};

const THEME = (dark) => dark ? {
  bg: '#0f1117', card: '#161922', text: '#e5e7eb', sub: '#7a8599',
  border: '#2a2f3d', accent: '#667eea', success: '#10b981',
} : {
  bg: '#ffffff', card: '#f9fafb', text: '#111827', sub: '#6b7280',
  border: '#e5e7eb', accent: '#667eea', success: '#10b981',
};

function SectionHead({ icon, title, t }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', paddingBottom: '8px', borderBottom: `1px solid ${t.border}` }}>
      {icon}
      <span style={{ fontSize: '11px', fontWeight: '700', color: t.text, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
    </div>
  );
}

function Toggle({ value, onChange, label, t }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
      <div onClick={() => onChange(!value)} style={{
        width: '32px', height: '17px', borderRadius: '9px', position: 'relative', cursor: 'pointer',
        background: value ? t.accent : t.border, transition: 'background .2s',
      }}>
        <div style={{ position: 'absolute', top: '2px', left: value ? '17px' : '2px', width: '13px', height: '13px',
          borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
      </div>
      <span style={{ fontSize: '11px', color: t.sub }}>{label}</span>
    </label>
  );
}

function TagRangeRow({ label, value, onChange, availableTags, darkMode, t }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ fontSize: '11px', fontWeight: '600', color: t.sub, marginBottom: '4px' }}>{label}</div>
      <SearchableTagSelect value={value.tagName} onChange={v => onChange({ ...value, tagName: v })}
        tags={availableTags} placeholder="Select tag…" darkMode={darkMode} />
      <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '10px', color: t.sub, marginBottom: '2px' }}>Min</div>
          <input type="number" value={value.min}
            onChange={e => onChange({ ...value, min: parseFloat(e.target.value) || 0 })}
            style={{ width: '100%', padding: '6px 8px', background: t.card, border: `1px solid ${t.border}`,
              borderRadius: '8px', color: t.text, fontSize: '11px', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '10px', color: t.sub, marginBottom: '2px' }}>Max</div>
          <input type="number" value={value.max}
            onChange={e => onChange({ ...value, max: parseFloat(e.target.value) || 100 })}
            style={{ width: '100%', padding: '6px 8px', background: t.card, border: `1px solid ${t.border}`,
              borderRadius: '8px', color: t.text, fontSize: '11px', outline: 'none', boxSizing: 'border-box' }} />
        </div>
      </div>
    </div>
  );
}

export default function ControlValveConfigModal({ component, availableTags, onSave, onClose, darkMode }) {
  const ex = component?.controlValveConfig || {};
  const t = THEME(darkMode);

  const [label,         setLabel]         = useState(component?.label || '');
  const [defaultColor,  setDefaultColor]  = useState(component?.defaultColor || '#64748b');
  const [displayTrend,  setDisplayTrend]  = useState(component?.displayTrend || false);
  const [showFaceplate, setShowFaceplate] = useState(component?.showFaceplate || false);
  const [showStatusLabel, setShowStatusLabel] = useState(component?.showStatusLabel !== false);

  const [pv,   setPv]   = useState({ ...DEFAULT.pv,   ...(ex.pv   || {}) });
  const [sp,   setSp]   = useState({ ...DEFAULT.sp,   ...(ex.sp   || {}) });
  const [op,   setOp]   = useState({ ...DEFAULT.op,   ...(ex.op   || {}) });
  const [openColor,  setOpenColor]  = useState(ex.openColor  ?? DEFAULT.openColor);
  const [closeColor, setCloseColor] = useState(ex.closeColor ?? DEFAULT.closeColor);
  const [mode, setMode] = useState({ ...DEFAULT.mode, ...(ex.mode || {}) });

  const handleSave = () => {
    onSave({
      ...component, label, defaultColor, displayTrend, showFaceplate, showStatusLabel,
      controlValveConfig: { pv, sp, op, openColor, closeColor, mode }
    });
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
      <div style={{ background: t.bg, borderRadius: '14px', width: '100%', maxWidth: '780px', maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: `0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px ${t.border}`, border: `1px solid ${t.border}` }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: `${t.accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Settings size={16} color={t.accent} />
            </div>
            <span style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>Configure Control Valve</span>
          </div>
          <button onClick={onClose} style={{ width: '28px', height: '28px', borderRadius: '8px', border: `1px solid ${t.border}`, background: 'rgba(255,255,255,0.05)', color: t.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: 1, padding: 0 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>

            {/* Inputs */}
            <div style={{ background: t.card, borderRadius: '10px', padding: '14px', border: `1px solid ${t.border}` }}>
              <SectionHead icon={<Activity size={12} color={t.accent} />} title="Inputs" t={t} />
              <TagRangeRow label="PV (Process Variable)" value={pv} onChange={setPv}
                availableTags={availableTags} darkMode={darkMode} t={t} />
              <TagRangeRow label="SP (Setpoint)" value={sp} onChange={setSp}
                availableTags={availableTags} darkMode={darkMode} t={t} />
              <TagRangeRow label="OP (Output)" value={op} onChange={setOp}
                availableTags={availableTags} darkMode={darkMode} t={t} />
            </div>

            {/* Control */}
            <div style={{ background: t.card, borderRadius: '10px', padding: '14px', border: `1px solid ${t.border}` }}>
              <SectionHead icon={<Settings size={12} color="#06b6d4" />} title="Control" t={t} />

              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: t.sub, marginBottom: '6px' }}>Valve open color</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ position: 'relative', width: '32px', height: '32px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: openColor, border: '2px solid rgba(255,255,255,0.1)', boxShadow: `0 0 10px ${openColor}44` }} />
                    <input type="color" value={openColor} onChange={e => setOpenColor(e.target.value)}
                      style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
                  </div>
                  <span style={{ fontSize: '11px', color: t.sub }}>OP ≥ 1 → open</span>
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: t.sub, marginBottom: '6px' }}>Valve close color</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ position: 'relative', width: '32px', height: '32px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: closeColor, border: '2px solid rgba(255,255,255,0.1)', boxShadow: `0 0 10px ${closeColor}44` }} />
                    <input type="color" value={closeColor} onChange={e => setCloseColor(e.target.value)}
                      style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
                  </div>
                  <span style={{ fontSize: '11px', color: t.sub }}>OP = 0 → closed</span>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '11px', fontWeight: '600', color: t.sub, marginBottom: '4px' }}>Mode</div>
                <SearchableTagSelect value={mode.tagName} onChange={v => setMode({ ...mode, tagName: v })}
                  tags={availableTags} placeholder="Select tag…" darkMode={darkMode} />
              </div>
            </div>

            {/* Faceplate */}
            <div style={{ background: t.card, borderRadius: '10px', padding: '14px', border: `1px solid ${t.border}` }}>
              <SectionHead icon={<Monitor size={12} color="#8b5cf6" />} title="Faceplate" t={t} />

              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: t.sub, marginBottom: '4px' }}>Label</div>
                <input value={label} onChange={e => setLabel(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', background: t.bg, border: `1px solid ${t.border}`,
                    borderRadius: '8px', color: t.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: t.sub, marginBottom: '4px' }}>Default color</div>
                <div style={{ position: 'relative', width: '100%', height: '32px' }}>
                  <div style={{ width: '100%', height: '32px', borderRadius: '8px', background: defaultColor, border: `1px solid ${t.border}` }} />
                  <input type="color" value={defaultColor} onChange={e => setDefaultColor(e.target.value)}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
                </div>
              </div>

              <Toggle value={showFaceplate} onChange={setShowFaceplate} label="Enable faceplate" t={t} />
              <Toggle value={displayTrend} onChange={setDisplayTrend} label="Show trend chart" t={t} />
              <Toggle value={showStatusLabel} onChange={setShowStatusLabel} label="Show status label" t={t} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${t.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', background: 'transparent', border: `1px solid ${t.border}`, borderRadius: '8px', color: t.sub, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Cancel</button>
          <button onClick={handleSave} style={{ padding: '8px 24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: '600', boxShadow: '0 2px 8px rgba(102,126,234,0.4)' }}>Save</button>
        </div>
      </div>
    </div>
  );
}
